import { isChatV8PayloadKind } from "@lattice/core-lib/types/chat-v8";
import type { T_ChatV8RenderableItem } from "@lattice/core-lib/chat-v8/materialize";
import { getChatV8RenderableItemKey } from "@lattice/core-lib/chat-v8/materialize";

export type T_TranscriptRow = {
  item: T_ChatV8RenderableItem;
  key: string;
  /** True for the first assistant-visible item after each user message. */
  showPrismHeader: boolean;
  /** Every tool step in this run; `item` is always the latest step. */
  stepItems: T_ChatV8RenderableItem[] | null;
};

function getPrismStepsGroupKey(item: T_ChatV8RenderableItem): string {
  const runCuid = item.primaryEvent.runCuid;
  return runCuid === null
    ? `turn:${item.primaryEvent.turnCuid}`
    : `run:${runCuid}`;
}

function isHiddenThinkingPart(item: T_ChatV8RenderableItem): boolean {
  return (
    item.kind === "message_part" &&
    isChatV8PayloadKind(item.payload, "message_part") &&
    item.payload.messagePartRole === "thinking"
  );
}

/**
 * Converts the event materializer's item stream into height-stable transcript
 * rows. A model run owns one Prism-steps row: subsequent tool activity updates
 * that row's latest item instead of appending another row to the FlatList.
 */
export function buildTranscriptRows(
  items: T_ChatV8RenderableItem[],
): T_TranscriptRow[] {
  const rows: T_TranscriptRow[] = [];
  let awaitingPrismHeader = false;

  const stepItemsByGroup = new Map<string, T_ChatV8RenderableItem[]>();

  for (const item of items) {
    if (item.kind !== "tool_card") continue;
    const groupKey = getPrismStepsGroupKey(item);
    const groupedItems = stepItemsByGroup.get(groupKey) ?? [];
    groupedItems.push(item);
    stepItemsByGroup.set(groupKey, groupedItems);
  }

  const emittedStepGroups = new Set<string>();

  for (const item of items) {
    if (isHiddenThinkingPart(item)) continue;

    if (item.kind === "user_text") {
      awaitingPrismHeader = true;
      rows.push({
        item,
        key: getChatV8RenderableItemKey(item),
        showPrismHeader: false,
        stepItems: null,
      });
      continue;
    }

    if (item.kind === "tool_card") {
      const groupKey = getPrismStepsGroupKey(item);
      if (emittedStepGroups.has(groupKey)) continue;
      emittedStepGroups.add(groupKey);

      const stepItems = stepItemsByGroup.get(groupKey) ?? [item];
      const latestStep = stepItems[stepItems.length - 1] ?? item;
      rows.push({
        item: latestStep,
        key: `prism-steps:${groupKey}`,
        showPrismHeader: false,
        stepItems,
      });
      continue;
    }

    const isAssistantVisible =
      item.kind === "message_part" || item.kind === "elicitation";
    rows.push({
      item,
      key: getChatV8RenderableItemKey(item),
      showPrismHeader: awaitingPrismHeader && isAssistantVisible,
      stepItems: null,
    });
    if (awaitingPrismHeader && isAssistantVisible) awaitingPrismHeader = false;
  }

  return rows;
}
